// A member's week of driving: totals, a week picker and each day's drives,
// which open that day's history with the drive selected.
import type { Schemas } from "@10twenty/api";
import { useQuery } from "@tanstack/react-query";
import { useMemo } from "react";
import { Link, useParams, useSearchParams } from "react-router";
import { api, ApiError, errorMessage, unwrap } from "../api";
import { useAuth } from "../auth/AuthContext";
import { dayKey, durationText, historyPath, shiftDay, timeText } from "../history/history";
import {
  driveDays,
  drivePath,
  firstDayOfWeek,
  resolveWeek,
  statsDayTitle,
  totalDistanceText,
  weekOf,
  weekTitle,
  type WeeklyDrive,
} from "../stats/stats";
import { distanceText, speedText, type Units } from "../units";
import { useUnits } from "../useUnits";
import { useNow } from "../useNow";

type StatsResult = { stats: Schemas["WeeklyStats"] } | { denied: ApiError };

/** The current week is reloaded this often while shown, like today's history. */
const REFRESH_MS = 60_000;
const TICK_MS = 60_000;

export function DrivingPage() {
  const { circleId = "", userId = "" } = useParams();
  const [search, setSearch] = useSearchParams();
  const { session } = useAuth();
  const nowMs = useNow(TICK_MS);
  const units = useUnits();
  const firstDay = useMemo(() => firstDayOfWeek(), []);
  const thisWeek = weekOf(dayKey(new Date(nowMs)), firstDay);
  const week = resolveWeek(search.get("week"), nowMs, firstDay);

  const members = useQuery({
    queryKey: ["circle", circleId, "members"],
    queryFn: async () => unwrap(await api.GET("/circles/{circleId}/members", { params: { path: { circleId } } })),
  });
  const member = members.data?.find((m) => m.user_id === userId);
  const name = member ? (member.user_id === session?.userId ? `${member.name} (you)` : member.name) : "Member";

  const stats = useQuery({
    queryKey: ["circle", circleId, "stats", userId, week],
    queryFn: async ({ signal }): Promise<StatsResult> => {
      try {
        const result = await api.GET("/circles/{circleId}/members/{userId}/stats", {
          params: { path: { circleId, userId }, query: { week } },
          signal,
        });
        return { stats: unwrap(result) };
      } catch (error) {
        // As in history: losing access replaces cached totals at once.
        if (error instanceof ApiError && (error.status === 403 || error.status === 404)) return { denied: error };
        throw error;
      }
    },
    refetchInterval: week === thisWeek ? REFRESH_MS : false,
  });
  const denied = stats.data && "denied" in stats.data ? stats.data.denied : null;
  const data = stats.data && "stats" in stats.data ? stats.data.stats : undefined;
  const paused = data?.sharing === "paused";
  const days = useMemo(() => (data && !paused ? driveDays(data) : []), [data, paused]);

  const goTo = (next: string) => setSearch(next === thisWeek ? {} : { week: next }, { replace: true });

  return (
    <div className="stack driving-page">
      <p>
        <Link to={historyPath(circleId, userId)}>← History</Link>
      </p>
      <h1>{name} · Driving</h1>
      <div className="driving-week-bar">
        <button type="button" className="secondary" onClick={() => goTo(shiftDay(week, -7))} aria-label="Earlier week">
          ‹
        </button>
        <label className="history-day">
          <span>{weekTitle(week, nowMs, firstDay)}</span>
          <input
            type="date"
            value={week}
            max={dayKey(new Date(nowMs))}
            required
            aria-label="Week containing"
            onChange={(event) => {
              if (event.target.value) goTo(resolveWeek(event.target.value, Date.now(), firstDay));
            }}
          />
        </label>
        <button
          type="button"
          className="secondary"
          onClick={() => goTo(shiftDay(week, 7))}
          disabled={week >= thisWeek}
          aria-label="Later week"
        >
          ›
        </button>
      </div>
      <div role="status">
        {denied ? (
          <p className="error">
            {denied.status === 404 ? "This member isn't in this circle, or you aren't." : errorMessage(denied)}
          </p>
        ) : stats.isPending ? (
          <p className="muted">Loading driving…</p>
        ) : !data ? (
          <p className="error">{errorMessage(stats.error)}</p>
        ) : paused ? (
          <p className="muted">{name} has paused location sharing.</p>
        ) : (
          <>
            {stats.error && <p className="error">{errorMessage(stats.error)}</p>}
            <Totals totals={data.totals} units={units} />
            {days.length === 0 && <p className="muted">No drives this week.</p>}
          </>
        )}
      </div>
      {days.map(({ day, drives }) => (
        <section key={day.day} className="card driving-day">
          <h2>
            {statsDayTitle(day.day)}
            <span className="muted">
              {" "}
              · {day.drive_count} {day.drive_count === 1 ? "drive" : "drives"} ·{" "}
              {distanceText(day.drive_distance_m, units)}
            </span>
          </h2>
          {drives.length === 0 ? (
            <p className="muted">Only the day&apos;s totals are kept now; its drives are no longer in history.</p>
          ) : (
            <ul className="driving-drives">
              {drives.map((drive) => (
                <DriveItem key={drive.start} circleId={circleId} userId={userId} drive={drive} units={units} />
              ))}
            </ul>
          )}
        </section>
      ))}
      {data && !paused && (
        <p className="hint">
          Days follow {member?.name ?? "the member"}&apos;s time zone ({data.time_zone}).
        </p>
      )}
    </div>
  );
}

function Totals({ totals, units }: { totals: Schemas["DrivingTotals"]; units: Units }) {
  const tiles: [string, string][] = [
    ["Distance", totalDistanceText(totals.drive_distance_m, units)],
    ["Drives", String(totals.drive_count)],
    ["Time driving", totals.drive_seconds === 0 ? "0 min" : durationText(totals.drive_seconds * 1000)],
    ["Top speed", totals.top_speed_mps === null ? "—" : speedText(totals.top_speed_mps, units)],
  ];
  return (
    <dl className="driving-totals">
      {tiles.map(([label, value]) => (
        <div key={label} className="card">
          <dt className="muted">{label}</dt>
          <dd>{value}</dd>
        </div>
      ))}
    </dl>
  );
}

function DriveItem({
  circleId,
  userId,
  drive,
  units,
}: {
  circleId: string;
  userId: string;
  drive: WeeklyDrive;
  units: Units;
}) {
  const start = Date.parse(drive.start);
  const end = Date.parse(drive.end);
  return (
    <li>
      <Link to={drivePath(circleId, userId, drive)} className="driving-drive">
        <span className="history-entry-title">
          {distanceText(drive.distance_m, units)}
          {drive.top_speed_mps !== null && ` · up to ${speedText(drive.top_speed_mps, units)}`}
        </span>
        <span className="history-entry-meta">
          {timeText(start)} – {timeText(end)} · {durationText(end - start)}
        </span>
      </Link>
    </li>
  );
}
